/**
 * CategoryAttributesCacheContext
 *
 * Lightweight cache for category attributes fetched by getProductsByCategory.
 * Allows CategoryPreview (writer) and CategoryConfigPanel (reader) to share
 * attribute data without a separate API call.
 */

import React, { createContext, useContext, useCallback, useRef, useState, ReactNode } from 'react';

interface CategoryAttributesCacheValue {
  getAttributes: (categoryId: string) => any[];
  setAttributes: (categoryId: string, attributes: any[]) => void;
}

const CategoryAttributesCacheContext = createContext<CategoryAttributesCacheValue | null>(null);

export const CategoryAttributesCacheProvider = ({ children }: { children: ReactNode }) => {
  const cacheRef = useRef<Record<string, any[]>>({});
  const [, setTick] = useState(0);

  const setAttributes = useCallback((categoryId: string, attributes: any[]) => {
    cacheRef.current[categoryId] = attributes;
    setTick((t) => t + 1);
  }, []);

  const getAttributes = useCallback((categoryId: string): any[] => {
    return cacheRef.current[categoryId] || [];
  }, []);

  return (
    <CategoryAttributesCacheContext.Provider value={{ getAttributes, setAttributes }}>
      {children}
    </CategoryAttributesCacheContext.Provider>
  );
};

export const useCategoryAttributesCache = (): CategoryAttributesCacheValue => {
  const ctx = useContext(CategoryAttributesCacheContext);
  if (!ctx) {
    throw new Error('useCategoryAttributesCache must be used within CategoryAttributesCacheProvider');
  }
  return ctx;
};
